import React from 'react';
import { CheckIcon, GiftIcon } from 'lucide-react';
import { Section } from './Shared';
import type { Volume } from '../types/product';
import { Bellflower, Blossom, Daisy, FlowerSpike } from './FloralDecor';
import { volumes } from '../data/volumes';
import { bonuses } from '../data/bonuses';
const volumeThemes: Record<Volume['theme'], {
  band: string;
  check: string;
  note: string;
}> = {
  green: {
    band: 'bg-emerald-800',
    check: 'bg-emerald-100 text-emerald-800',
    note: 'bg-emerald-50 text-emerald-900'
  },
  rose: {
    band: 'bg-rose-800',
    check: 'bg-rose-100 text-rose-800',
    note: 'bg-rose-50 text-rose-900'
  },
  plum: {
    band: 'bg-purple-900',
    check: 'bg-purple-100 text-purple-800',
    note: 'bg-purple-50 text-purple-900'
  }
};
export function PackContents() {
  return <Section id="contents" className="relative" data-id="element-200" data-mp="wrn9ejbba9wh">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden" data-id="element-201" data-mp="wqvd169w4855">
        <Blossom className="absolute right-[4%] top-10 h-24 w-24" petalClassName="text-pink-200" centerClassName="text-amber-300" data-id="element-202" data-mp="3p7e6f0eooqtv" />
        <Daisy className="absolute left-[3%] top-12 h-16 w-16" petalClassName="text-amber-100" centerClassName="text-amber-300" data-id="element-203" data-mp="3k92wyqsawic6" />
        <Bellflower className="absolute left-[1%] top-[40%] hidden h-40 w-20 lg:block" petalClassName="text-violet-200" centerClassName="text-emerald-200" data-id="element-204" data-mp="2tcw2mim4u5v" />
        <FlowerSpike className="absolute bottom-[18%] right-[1%] hidden h-40 w-12 lg:block" petalClassName="text-fuchsia-200" centerClassName="text-emerald-200" data-id="element-205" data-mp="brynz89z5ljb" />
      </div>

      <div className="mx-auto mb-14 max-w-4xl text-center md:mb-16" data-id="element-206" data-mp="6vrjljx88i3h">
        <p className="text-sm font-semibold text-amber-800 md:text-base" data-id="element-207" data-mp="lq5tmuytch60">
          Una biblioteca completa per la consulenza floreale
        </p>
        <p className="mt-1 text-sm text-stone-500 md:text-base" data-id="element-208" data-mp="2nhr5ywe4x25v">
          Per chi studia e per chi desidera organizzare meglio la propria pratica
        </p>
        <h2 className="mt-5 text-3xl font-bold tracking-tight text-emerald-900 md:text-5xl" data-id="element-209" data-mp="2ikse6khy17au">
          2 volumi principali + 5 bonus pratici
        </h2>
        <p className="mt-2 text-xl font-bold tracking-tight text-rose-700 md:text-3xl" data-id="element-210" data-mp="3c7wx2y660cdy">
          + Mappa Rapida dei 38 Fiori di Bach
        </p>
        <p className="mx-auto mt-5 max-w-2xl text-base font-medium leading-relaxed text-stone-900 md:text-lg" data-id="element-211" data-mp="3h6gmj8jyw6b2">
          188 pagine tra contenuti e strumenti visivi: ciò che ti serve, organizzato e facile da consultare.
        </p>
        <div className="mx-auto mt-6 max-w-2xl space-y-4 border-t border-stone-200 pt-6 text-base leading-relaxed text-stone-700 md:text-lg" data-id="element-212" data-mp="3v7au5oz6y38o">
          <p data-id="element-213" data-mp="3u6erqn540cp">
            Che tu stia approfondendo il sistema Bach o svolga già consulenze floreali, avere una struttura ti aiuta a
            lavorare con maggiore chiarezza.
          </p>
          <p data-id="element-214" data-mp="ds464n0aqd9j">
            Il Kit Completo Fiori di Bach™ nasce per collegare ascolto, confronto dei rimedi, proposta personalizzata e
            follow-up.
          </p>
          <p data-id="element-215" data-mp="238pugd155pn4">
            Perché conoscere i fiori è il punto di partenza. Saper organizzare il ragionamento rende il tuo lavoro più
            comprensibile anche alla persona che hai davanti.
          </p>
        </div>
      </div>

      <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-2" data-id="element-216" data-mp="1tas8jw6r84xo">
        {volumes.map(volume => {
        const theme = volumeThemes[volume.theme];
        return <article key={volume.label} className="flex h-full flex-col overflow-hidden rounded-xl border border-amber-400/70 bg-white shadow-md" data-id="element-217" data-mp="2lyelvw5sanxq">
              <div className={`relative flex gap-4 overflow-hidden p-5 ${theme.band}`} data-id="element-218" data-mp="2h03bnkbkubez">
                <Blossom className="pointer-events-none absolute -right-5 -top-5 h-20 w-20" petalClassName="text-white/10" centerClassName="text-amber-300/30" data-id="element-219" data-mp="2c7jygogsxiap" />
                <div className="relative w-24 shrink-0 self-start overflow-hidden rounded-sm shadow-lg ring-2 ring-amber-300/80" data-id="element-220" data-mp="34ow2x3h97v39">
                  <img src={volume.cover} alt={volume.coverAlt} loading="lazy" className="block h-auto w-full" draggable={false} data-id="element-221" data-mp="2zxe8ky9qksr9" />
                </div>
                <div className="relative min-w-0" data-id="element-222" data-mp="2pfiuoyspn9kb">
                  <span className="inline-block rounded-full bg-amber-300 px-2 py-0.5 text-xs font-bold tracking-widest text-stone-900" data-id="element-223" data-mp="2pgaelccy3iur">
                    {volume.label}
                  </span>
                  <h3 className="mt-2 text-lg font-bold leading-snug text-white" data-id="element-224" data-mp="2fi4en0ap8msj">{volume.title}</h3>
                  <p className="mt-1 text-sm leading-snug text-white/80" data-id="element-225" data-mp="3388gp388oehf">{volume.summary}</p>
                </div>
              </div>
              <div className="flex flex-1 flex-col p-5" data-id="element-226" data-mp="3883c9k2yls8t">
                <ul className="space-y-2 text-sm leading-snug text-stone-900" data-id="element-227" data-mp="2tg2uka4o4ra5">
                  {volume.features.map(feature => <li key={feature} className="flex gap-2.5" data-id="element-228" data-mp="2yemfc77izx0x">
                      <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${theme.check}`} aria-hidden="true" data-id="element-229" data-mp="3m59oevt6gsp2">
                        <CheckIcon className="h-3 w-3" strokeWidth={3} data-id="element-230" data-mp="3qxt4bzfc7zx1" />
                      </span>
                      <span data-id="element-231" data-mp="1ffokxs5bt9dp">{feature}</span>
                    </li>)}
                </ul>
                {volume.note && <div className="mt-auto pt-4" data-id="element-232" data-mp="1agbxb56mrya2">
                    <p className={`rounded-md px-3 py-2 text-sm italic leading-snug ${theme.note}`} data-id="element-233" data-mp="1p7lukugortab">{volume.note}</p>
                  </div>}
              </div>
            </article>;
      })}
      </div>

      <div className="mb-8 mt-16 text-center md:mt-20" data-id="element-234" data-mp="whvfai3bilcm">
        <h3 className="flex flex-wrap items-center justify-center gap-x-2 text-2xl font-bold tracking-tight text-emerald-900 md:text-3xl" data-id="element-235" data-mp="wkhxrcrqyl9m">
          <GiftIcon className="h-7 w-7 text-amber-700" aria-hidden="true" data-id="element-236" data-mp="15jli0k2nh6pp" />
          5 bonus pratici — <span className="text-amber-800" data-id="element-237" data-mp="11h8bvujzr6se">sempre inclusi</span>
        </h3>
      </div>

      <ul className="mx-auto grid max-w-6xl gap-3 sm:grid-cols-2 lg:grid-cols-5" data-id="element-238" data-mp="2gy8imatftwth">
        {bonuses.map(bonus => <li key={bonus.number} className="flex gap-3 rounded-xl border border-stone-200 bg-white p-3 shadow-sm lg:flex-col lg:gap-0 lg:p-4" data-id="element-239" data-mp="2cxg71ax3ffu1">
            <div className="w-16 shrink-0 lg:mx-auto lg:mb-3 lg:w-28" data-id="element-240" data-mp="19lhg0tzbk3a8">
              <img src={bonus.cover} alt={`Copertina del Bonus ${bonus.number}: ${bonus.title}`} loading="lazy" className="h-auto w-full rounded-sm shadow-md" draggable={false} data-id="element-241" data-mp="1ekauopqce0bz" />
            </div>
            <div className="flex min-w-0 flex-1 flex-col" data-id="element-242" data-mp="1nm22q18cuj90">
              <p className="text-xs font-semibold tracking-widest text-amber-800" data-id="element-243" data-mp="1njfus0hjy1q9">BONUS {bonus.number}</p>
              <h4 className="mt-0.5 text-sm font-bold leading-snug text-emerald-900" data-id="element-244" data-mp="vum75hotlm6n">{bonus.title}</h4>
              <p className="mt-1 text-xs leading-snug text-stone-700" data-id="element-245" data-mp="zw7n5bwnigl2">{bonus.description}</p>
              <p className="mt-auto pt-2 text-xs font-semibold text-emerald-700" data-id="element-246" data-mp="14mwg6vnzblmb">{bonus.pages}</p>
            </div>
          </li>)}
      </ul>
    </Section>;
}